import { Fragment } from 'react';

/**
 * API / CSV katalog alanları İngilizce; kartlarda seçilen dile gösterim.
 * @param {(en: string, fr: string, es: string, ar: string) => string} tl
 */

/**
 * Renders text with a standard sans-serif ampersand inside `.font-serif` (Playfair) headings.
 */
export function TitleWithPlainAmpersand({ children, as: Tag = 'span', className = '', ...rest }) {
  const text = typeof children === 'string' ? children : String(children ?? '');
  if (!text.includes('&')) {
    return (
      <Tag className={className} {...rest}>
        {children}
      </Tag>
    );
  }
  const parts = text.split('&');
  return (
    <Tag className={className} {...rest}>
      {parts.map((part, i) => (
        <Fragment key={i}>
          {i > 0 ? <span className="amp-plain">&amp;</span> : null}
          {part}
        </Fragment>
      ))}
    </Tag>
  );
}

/**
 * İngilizce `product.name` için bilinen çeviriler (CSV'de `name_tr` boş olduğunda).
 * Anahtar: trim + toLowerCase; tire/çizgi öncesi parça da denenir.
 */
const PRODUCT_NAME_LOCALE = {
  spaghetti: { fr: 'Spaghetti', es: 'Espaguetis', ar: 'سباغيتي' },
  'penne rigate': { fr: 'Penne rigate', es: 'Penne rigate', ar: 'بيني ريجاتي' },
  fusilli: { fr: 'Fusilli', es: 'Fusilli', ar: 'فوسيلي' },
  farfalle: { fr: 'Farfalle', es: 'Farfalle', ar: 'فارفاللي' },
  macaroni: { fr: 'Macaronis', es: 'Macarrones', ar: 'مكرونة' },
  elbow: { fr: 'Macaroni', es: 'Macarrones', ar: 'مكرونة' },
  noodle: { fr: 'Nouilles', es: 'Fideos', ar: 'نودلز' },
  'macaroni & cheese': { fr: 'Macaroni au fromage', es: 'Macarrones con queso', ar: 'مكرونة بالجبن' },
  lasagna: { fr: 'Lasagnes', es: 'Lasaña', ar: 'لازانيا' },
  lasagne: { fr: 'Lasagnes', es: 'Lasaña', ar: 'لازانيا' },
  tagliatelle: { fr: 'Tagliatelles', es: 'Tagliatelle', ar: 'تالياتيلي' },
  vermicelli: { fr: 'Vermicelles', es: 'Fideos', ar: 'شعيرية' },
  rigatoni: { fr: 'Rigatoni', es: 'Rigatoni', ar: 'ريغاتوني' },
  shells: { fr: 'Coquillettes', es: 'Conchas', ar: 'مكرونة أصداف' },
  conchiglie: { fr: 'Conchiglie', es: 'Conchiglie', ar: 'كونكيليي' },
  linguine: { fr: 'Linguine', es: 'Linguine', ar: 'لينغويني' },
  fettuccine: { fr: 'Fettuccine', es: 'Fettuccine', ar: 'فيتوتشيني' },
  ravioli: { fr: 'Raviolis', es: 'Ravioli', ar: 'رافيولي' },
  tortellini: { fr: 'Tortellini', es: 'Tortellini', ar: 'تورتيليني' },
  cannelloni: { fr: 'Cannelloni', es: 'Cañones', ar: 'كانيلوني' },
  gnocchi: { fr: 'Gnocchis', es: 'Ñoquis', ar: 'نيوكي' },
  orzo: { fr: 'Orzo', es: 'Orzo', ar: 'أورزو' },
  couscous: { fr: 'Couscous', es: 'Cuscús', ar: 'كسكس' },
  'thin spaghetti': { fr: 'Spaghetti fins', es: 'Espagueti fino', ar: 'سباغيتي رفيع' },
  capellini: { fr: 'Capellini', es: 'Capellini', ar: 'كابيليني' },
  bucatini: { fr: 'Bucatini', es: 'Bucatini', ar: 'بوكاتيني' },
  rotini: { fr: 'Rotini', es: 'Rotini', ar: 'روتيني' },
  rotelle: { fr: 'Rotelle', es: 'Rotelle', ar: 'روتيلي' },
  gemelli: { fr: 'Gemelli', es: 'Gemelli', ar: 'جيميلي' },
  orecchiette: { fr: 'Orecchiette', es: 'Orecchiette', ar: 'أوريكيتي' },
  'chicken flavored noodle': { fr: 'Nouilles saveur Poulet', es: 'Fideos sabor pollo', ar: 'نودلز بنكهة الدجاج' },
  'curry flavored noodle': { fr: 'Nouilles saveur Curry', es: 'Fideos sabor curry', ar: 'نودلز بنكهة الكاري' },
  'tomato flavored noodle': { fr: 'Nouilles saveur Tomate', es: 'Fideos sabor tomate', ar: 'نودلز بنكهة الطماطم' },
  'vegetable flavored noodle': { fr: 'Nouilles saveur légumes', es: 'Fideos sabor verduras', ar: 'نودلز بنكهة الخضار' },
  'baklava flour': { fr: 'Farine spéciale Baklava', es: 'Harina especial baklava', ar: 'دقيق بقلاوة خاص' },
  'biscuit flour': { fr: 'Farine pour Biscuits', es: 'Harina para galletas', ar: 'دقيق بسكويت' },
  'chapati flour': { fr: 'Farine pour Chapati', es: 'Harina para chapati', ar: 'دقيق تشاباتي' },
  'french baguette flour': { fr: 'Farine pour Baguette française', es: 'Harina para baguette francesa', ar: 'دقيق باغيت فرنسي' },
  'parotta maida': { fr: 'Farine Maida pour parotta', es: 'Harina maida para parotta', ar: 'دقيق مايدا للباروتا' },
  'pizza flour': { fr: 'Farine à Pizza', es: 'Harina para pizza', ar: 'دقيق بيتزا' },
  'tandoor & roti flour': { fr: 'Farine pour Tandoor & Roti', es: 'Harina para tandoor y roti', ar: 'دقيق تندور وروتي' },
  'coarse bulgur': { fr: 'Boulgour gros grain', es: 'Bulgur grueso', ar: 'برغل خشن' },
  'coarse bulgur with vermicelli': { fr: 'Boulgour gros grains aux Vermicelles', es: 'Bulgur grueso con fideos', ar: 'برغل خشن مع شعيرية' },
  'fine bulgur': { fr: 'Boulgour Fin', es: 'Bulgur fino', ar: 'برغل ناعم' },
  'dessert semolina': { fr: 'Semoule pour dessert', es: 'Sémola para postre', ar: 'سميد للحلويات' },
  'pasta semolina': { fr: 'Semoule pour pâtes', es: 'Sémola para pasta', ar: 'سميد للمعكرونة' },
  'sunflower oil': { fr: 'Huile de Tournesol', es: 'Aceite de girasol', ar: 'زيت دوار الشمس' },
  'extra virgin olive oil': { fr: "Huile d'olive extra vierge", es: 'Aceite de oliva virgen extra', ar: 'زيت زيتون بكر ممتاز' },
  'pomace olive oil': { fr: "Huile de grignons d'olive", es: 'Aceite de orujo de oliva', ar: 'زيت زيتون من البقايا' },
  'dried apricot': { fr: 'Abricots secs', es: 'Albaricoques secos', ar: 'مشمش مجفف' },
  'dried fig': { fr: 'Figues secs', es: 'Higos secos', ar: 'تين مجفف' },
  'dried grape (raisin)': { fr: 'Raisins secs', es: 'Uvas pasas', ar: 'زبيب' },
  'dried tomato': { fr: 'Tomates séchées', es: 'Tomates secos', ar: 'طماطم مجففة' },
  'roasted red pepper': { fr: 'Poivrons rouges grillés', es: 'Pimientos rojos asados', ar: 'فلفل أحمر مشوي' },
  'roasted eggplant': { fr: 'Aubergines grillées', es: 'Berenjenas asadas', ar: 'باذنجان مشوي' },
  'beetroot pickles': { fr: 'Betteraves au vinaigre', es: 'Remolacha en vinagre', ar: 'شمندر مخلل' },
  'cabbage pickles': { fr: 'Pickles de chou blanc', es: 'Col encurtida', ar: 'مخلل ملفوف' },
  'cucumber pickles': { fr: 'Cornichons', es: 'Pepinillos', ar: 'مخلل خيار' },
  'garlic pickles': { fr: 'Ail au vinaigre', es: 'Ajo en vinagre', ar: 'ثوم مخلل' },
  'hot pepper pickles': { fr: 'Piments forts au vinaigre', es: 'Chile picante en vinagre', ar: 'فلفل حار مخلل' },
  'jalapeno pickles': { fr: 'Jalapeños au vinaigre', es: 'Jalapeños en vinagre', ar: 'هالابينو مخلل' },
  'mixed vegetable pickles': { fr: 'Légumes mélangés au vinaigre', es: 'Verduras mixtas en vinagre', ar: 'خضار مخللة مشكلة' },
  'pepperoncini pickles': { fr: 'Pepperoncini au vinaigre', es: 'Pepperoncini en vinagre', ar: 'فلفل بيبرونشيني مخلل' },
  'apricot jam': { fr: "Confiture d'abricot", es: 'Mermelada de albaricoque', ar: 'مربى مشمش' },
  'blackberry jam': { fr: 'Confiture de mûre', es: 'Mermelada de mora', ar: 'مربى توت' },
  'fig jam': { fr: 'Confiture de figue', es: 'Mermelada de higo', ar: 'مربى تين' },
  'orange jam': { fr: "Confiture d'orange", es: 'Mermelada de naranja', ar: 'مربى برتقال' },
  'sour cherry jam': { fr: 'Confiture de griotte', es: 'Mermelada de cereza ácida', ar: 'مربى كرز حامض' },
  'strawberry jam': { fr: 'Confiture de fraise', es: 'Mermelada de fresa', ar: 'مربى فراولة' },
  'chewing gum': { fr: 'Chewing-gum', es: 'Chicle', ar: 'علكة' },
  chips: { fr: 'Chips', es: 'Patatas fritas', ar: 'رقائق بطاطس' },
  cornflakes: { fr: 'Cornflakes', es: 'Copos de maíz', ar: 'كورن فليكس' },
  'himalayan salt': { fr: "Sel d'Himalaya", es: 'Sal del Himalaya', ar: 'ملح الهيمالايا' },
  'road salt': { fr: 'Sel de déneigement', es: 'Sal para carreteras', ar: 'ملح طرق' },
  'rock salt': { fr: 'Sel gemme', es: 'Sal gema', ar: 'ملح صخري' },
  'sea salt': { fr: 'Sel de mer', es: 'Sal de mar', ar: 'ملح بحري' },
  'apricot juice': { fr: "Jus d'abricot", es: 'Zumo de albaricoque', ar: 'عصير مشمش' },
  'pomegranate juice': { fr: 'Jus de grenade', es: 'Zumo de granada', ar: 'عصير رمان' },
  'apple juice': { fr: 'Jus de pomme 100%', es: 'Zumo de manzana 100%', ar: 'عصير تفاح 100٪' },
  'grape juice': { fr: 'Jus de raisin 100%', es: 'Zumo de uva 100%', ar: 'عصير عنب 100٪' },
  'mix fruit juice': { fr: 'Jus de fruits mélangés 100%', es: 'Zumo de frutas mixtas 100%', ar: 'عصير فواكه مشكلة 100٪' },
  'orange juice': { fr: "Jus d'orange 100%", es: 'Zumo de naranja 100%', ar: 'عصير برتقال 100٪' },
  'pineapple juice': { fr: "Jus d'ananas 100%", es: 'Zumo de piña 100%', ar: 'عصير أناناس 100٪' },
  'tomato juice': { fr: 'Jus de Tomate 100%', es: 'Zumo de tomate 100%', ar: 'عصير طماطم 100٪' },
  'grape fruit drink': { fr: 'Boisson au raisin', es: 'Bebida de uva', ar: 'مشروب عنب' },
  'mango fruit drink': { fr: 'Boisson à la mangue', es: 'Bebida de mango', ar: 'مشروب مانجو' },
  'mixed fruit drink': { fr: 'Boisson multivitaminée', es: 'Bebida multivitamínica', ar: 'مشروب متعدد الفيتامينات' },
  'orange fruit drink': { fr: "Boisson à l'orange", es: 'Bebida de naranja', ar: 'مشروب برتقال' },
  'peach fruit drink': { fr: 'Boisson à la pêche', es: 'Bebida de melocotón', ar: 'مشروب خوخ' },
  'pineapple fruit drink': { fr: "Boisson à l'ananas", es: 'Bebida de piña', ar: 'مشروب أناناس' },
  'apricot nectar juice': { fr: "Nectar d'abricot", es: 'Néctar de albaricoque', ar: 'نكتار مشمش' },
  'guava nectar juice': { fr: 'Nectar de goyave', es: 'Néctar de guayaba', ar: 'نكتار جوافة' },
  'mango nectar juice': { fr: 'Nectar de mangue', es: 'Néctar de mango', ar: 'نكتار مانجو' },
  'mixture nectar juice': { fr: 'Nectar de fruits mélangés', es: 'Néctar de frutas mixtas', ar: 'نكتار فواكه مشكلة' },
  'peach nectar juice': { fr: 'Nectar de pêche', es: 'Néctar de melocotón', ar: 'نكتار خوخ' },
  'sour cherry nectar juice': { fr: 'Nectar de griotte', es: 'Néctar de cereza ácida', ar: 'نكتار كرز حامض' },
  'organic apple juice': { fr: 'Jus de pomme bio', es: 'Zumo de manzana ecológico', ar: 'عصير تفاح عضوي' },
  'organic grape juice': { fr: 'Jus de raisin bio', es: 'Zumo de uva ecológico', ar: 'عصير عنب عضوي' },
  'organic orange juice': { fr: "Jus d'orange bio", es: 'Zumo de naranja ecológico', ar: 'عصير برتقال عضوي' },
  'organic pomegranate juice': { fr: 'Jus de grenade bio', es: 'Zumo de granada ecológico', ar: 'عصير رمان عضوي' },
  'organic red beet juice': { fr: 'Jus de betterave bio', es: 'Zumo de remolacha ecológico', ar: 'عصير شمندر عضوي' },
  'organic red fruit juice': { fr: 'jus de fruits rouges bio', es: 'Zumo de frutos rojos ecológico', ar: 'عصير فواكه حمراء عضوي' },
  'organic tart cherry juice': { fr: 'Jus de griotte bio', es: 'Zumo de cereza ácida ecológico', ar: 'عصير كرز حامض عضوي' },
  'dishwasher brightener': { fr: 'Produit de rinçage pour lave-vaisselle', es: 'Abrillantador para lavavajillas', ar: 'ملمع غسالة صحون' },
  'drain cleaner': { fr: 'Déboucheur pour canalisations', es: 'Desatascador de tuberías', ar: 'منظف مصارف' },
  'bathroom cleaner': { fr: 'Désinfectant pour salle de bains', es: 'Desinfectante para baño', ar: 'مطهر حمام' },
  'kitchen cleaner': { fr: 'Dégraissant cuisine', es: 'Desengrasante de cocina', ar: 'منظف مطبخ' },
  'toilet paper': { fr: 'Papier toilette', es: 'Papel higiénico', ar: 'ورق تواليت' },
  'baby diaper': { fr: 'Couches bébé', es: 'Pañales bebé', ar: 'حفاضات أطفال' },
  'baby wet towel': { fr: 'Lingettes bébé', es: 'Toallitas bebé', ar: 'مناديل أطفال مبللة' },
  'handkerchief tissue': { fr: 'Mouchoirs en papier', es: 'Pañuelos de papel', ar: 'مناديل ورقية' },
  handkerchief: { fr: 'Mouchoirs en papier', es: 'Pañuelos de papel', ar: 'مناديل ورقية' },
  'napkin tissue': { fr: 'Serviette en papier', es: 'Servilleta de papel', ar: 'منديل ورقي' },
  'wet wipes': { fr: 'lingettes désinfectantes pour les mains', es: 'Toallitas desinfectantes', ar: 'مناديل مطهرة لليدين' },
  'laundry detergent': { fr: 'Lessive', es: 'Detergente ropa', ar: 'منظف غسيل' },
  'dishwashing detergent': { fr: 'Liquide vaisselle', es: 'Lavavajillas', ar: 'منظف صحون' },
  'paper towel': { fr: 'Essuie-tout', es: 'Papel cocina', ar: 'مناديل مطبخ' },
};

/** WooCommerce / CSV İngilizce ürün adı yazım düzeltmeleri (EN gösterim). */
const PRODUCT_NAME_EN_CORRECTIONS = {
  'all purpose flour': 'All-Purpose Flour',
  'chappati flour': 'Chapati Flour',
  'course bulgur with vermicelli': 'Coarse Bulgur with Vermicelli',
  'desert semolina': 'Dessert Semolina',
  'sourcherry jam': 'Sour Cherry Jam',
  'diswashing detergent': 'Dishwashing Detergent',
  'mix fruit drink': 'Mixed Fruit Drink',
  'ice coffee': 'Iced Coffee',
};

function correctEnglishProductName(name) {
  const raw = (name || '').trim();
  if (!raw) return raw;
  return PRODUCT_NAME_EN_CORRECTIONS[raw.toLowerCase()] || raw;
}

function productNameLocaleLookup(englishName, lang) {
  if (!englishName) return null;
  const lc = String(lang ?? 'en').toLowerCase();
  if (lc === 'en') return null;
  const rowFor = (key) => {
    const row = PRODUCT_NAME_LOCALE[key];
    if (!row) return null;
    return row[lc] || null;
  };
  const base = englishName.trim().toLowerCase();
  const firstSegment = base.split(/\s*[–\-|]\s*/)[0].trim();
  return rowFor(base) || (firstSegment !== base ? rowFor(firstSegment) : null);
}

function pickLocaleString(lang, en, fr, es, ar) {
  const lc = String(lang ?? 'en').toLowerCase();
  if (lc === 'fr') return fr;
  if (lc === 'es') return es;
  if (lc === 'ar') return ar;
  return en;
}

/** Woo `name` (EN) + isteğe bağlı `name_tr`; FR/ES/AR için önce katalog çevirisi, sonra gerekiyorsa Türkçe ticari ad. */
export function getProductDisplayName(product, lang) {
  if (!product) return '';
  const lc = String(lang ?? 'en').toLowerCase();
  const en = (product.name || '').trim();
  const tr = (product.name_tr || '').trim();

  const tryCatalogLabel = (label) => {
    const s = (label || '').trim();
    if (!s || lc === 'en') return null;
    const cat = (product.category || '').trim();
    const chip = catalogChipLabel(s, cat, (e, fr, es, ar) => pickLocaleString(lc, e, fr, es, ar));
    return chip !== s ? chip : null;
  };

  if (lc === 'en') {
    return correctEnglishProductName(en || tr || String(product.sku || product.id || '').trim());
  }

  const localized = productNameLocaleLookup(en, lc);
  if (localized) return localized;

  const fromSub = tryCatalogLabel(product.subcategory);
  if (fromSub) return fromSub;

  const fromName = tryCatalogLabel(en);
  if (fromName) return fromName;

  if (tr) return tr;
  return en || String(product.sku || product.id || '').trim();
}

export function catalogChipLabel(subcategory, category, tl) {
  const raw = (subcategory || category || '').trim();
  if (!raw) return '';
  if (/^uncategorized$/i.test(raw)) return '';

  const map = {
    Pasta: () => tl('Pasta', 'Pâtes', 'Pasta', 'معكرونة'),
    'Wheat Flour': () => tl('Wheat Flour', 'Farine de blé', 'Harina de trigo', 'دقيق القمح'),
    Bulgur: () => tl('Bulgur', 'Boulgour', 'Bulgur', 'برغل'),
    Semolina: () => tl('Semolina', 'Semoule', 'Sémola', 'سميد'),
    '100% Juice': () => tl('100% Juice', 'Jus 100 %', 'Jugo 100 %', 'عصير 100٪'),
    'Fruit Juices': () => tl('Fruit Juices', 'Jus de fruits', 'Zumos', 'عصائر فواكه'),
    'Tomato Paste': () => tl('Tomato Paste', 'Concentré de tomate', 'Pasta de tomate', 'معجون الطماطم'),
    'Edible Oils': () => tl('Edible Oils', 'Huiles alimentaires', 'Aceites comestibles', 'زيوت صالحة للأكل'),
    'Sunflower Oil': () => tl('Sunflower Oil', 'Huile de tournesol', 'Aceite de girasol', 'زيت دوار الشمس'),
    'Olive Oil': () => tl('Olive Oil', "Huile d'olive", 'Aceite de oliva', 'زيت الزيتون'),
    'Grape Leaves': () => tl('Grape Leaves', 'Feuilles de vigne', 'Hojas de parra', 'ورق عنب'),
    Pickles: () => tl('Pickles', 'Cornichons', 'Encurtidos', 'مخللات'),
    Marble: () => tl('Marble', 'Marbre', 'Mármol', 'رخام'),
    Cement: () => tl('Cement', 'Ciment', 'Cemento', 'أسمنت'),
    'Ceramic Tile': () =>
      tl('Ceramic & Porcelain Tile', 'Carrelage ceramique et porcelaine', 'Azulejo ceramico y porcelana', 'بلاط خزف وبورسلين'),
    'Ceramic & Porcelain Tile': () =>
      tl('Ceramic & Porcelain Tile', 'Carrelage ceramique et porcelaine', 'Azulejo ceramico y porcelana', 'بلاط خزف وبورسلين'),
    Paint: () => tl('Paint & Coating', 'Peinture et revetement', 'Pintura y revestimiento', 'دهان وطلاء'),
    'Paint & Coating': () => tl('Paint & Coating', 'Peinture et revetement', 'Pintura y revestimiento', 'دهان وطلاء'),
    'Fresh Vegetables & Fruits': () =>
      tl('Fresh Vegetables & Fruits', 'Legumes et fruits frais', 'Verduras y frutas frescas', 'خضروات وفواكه طازجة'),
    Uncategorized: () => tl('Uncategorized', 'Non classe', 'Sin categoria', 'غير مصنف'),
    'Laundry Detergent': () => tl('Laundry Detergent', 'Lessive', 'Detergente ropa', 'منظف غسيل'),
    'Toilet Paper': () => tl('Toilet Paper', 'Papier toilette', 'Papel higiénico', 'ورق تواليت'),
    'Baby Diaper': () => tl('Baby Diaper', 'Couche bébé', 'Pañal bebé', 'حفاض أطفال'),
    'Food & Beverages': () => tl('Food & Beverages', 'Alimentation', 'Alimentación', 'أغذية ومشروبات'),
    'Household Cleaning': () => tl('Household Cleaning', 'Entretien ménager', 'Limpieza hogar', 'تنظيف منزلي'),
    'Personal Care': () => tl('Personal Care', 'Soins personnels', 'Cuidado personal', 'عناية شخصية'),
    'Construction & Building': () => tl('Construction & Building', 'Construction', 'Construcción', 'إنشاءات'),
    Salt: () => tl('Salt', 'Sel', 'Sal', 'ملح'),
    'Roasted Eggplant': () => tl('Roasted Eggplant', 'Aubergine grillée', 'Berenjena asada', 'باذنجان مشوي'),
    'Roasted Red Pepper': () => tl('Roasted Red Pepper', 'Poivron grillé', 'Pimiento asado', 'فلفل أحمر مشوي'),
    Candy: () => tl('Candy', 'Confiseries', 'Dulces', 'حلويات'),
    'Chewing Gum': () => tl('Chewing Gum', 'Gomme à mâcher', 'Chicle', 'علكة'),
    Confectionery: () => tl('Confectionery', 'Confiseries', 'Confitería', 'حلويات'),
    'Energy Drink': () => tl('Energy Drink', 'Boisson énergisante', 'Bebida energética', 'مشروب طاقة'),
    'Dried Fruits': () => tl('Dried Fruits', 'Fruits secs', 'Frutos secos', 'فواكه مجففة'),
    'Dishwashing Detergent': () => tl('Dishwashing Detergent', 'Liquide vaisselle', 'Lavavajillas', 'منظف صحون'),
    'Dishwashing Tablet': () => tl('Dishwashing Tablet', 'Tablettes lave-vaisselle', 'Pastillas lavavajillas', 'أقراص غسالة صحون'),
    'Dish Brightener': () =>
      tl('Dishwasher Rinse Aid', 'Brillant lave-vaisselle', 'Abrillantador para lavavajillas', 'ملمع غسالة صحون'),
    'Dishwasher Rinse Aid': () =>
      tl('Dishwasher Rinse Aid', 'Brillant lave-vaisselle', 'Abrillantador para lavavajillas', 'ملمع غسالة صحون'),
    'Drain Opener': () => tl('Drain Opener', 'Déboucheur', 'Desatascador', 'فاتح تصريف'),
    'Fabric Softener': () => tl('Fabric Softener', 'Assouplissant', 'Suavizante', 'منعم أقمشة'),
    'Glass Cleaner': () => tl('Glass Cleaner', 'Nettoyant vitres', 'Limpiacristales', 'منظف زجاج'),
    'Surface Cleaner': () => tl('Surface Cleaner', 'Nettoyant surfaces', 'Limpiador superficies', 'منظف أسطح'),
    'Rust & Lime Remover': () => tl('Rust & Lime Remover', 'Antirouille et anticalcaire', 'Antióxido y antical', 'مزيل صدأ وكلس'),
    'Liquid Soap': () => tl('Liquid Soap', 'Savon liquide', 'Jabón líquido', 'صابون سائل'),
    'Paper Towel': () => tl('Paper Towel', 'Essuie-tout', 'Papel cocina', 'مناديل مطبخ'),
    'Wet Wipes': () => tl('Wet Wipes', 'Lingettes', 'Toallitas húmedas', 'مناديل مبللة'),
    'Sanitary Napkin': () => tl('Sanitary Napkin', 'Serviette hygiénique', 'Compresa', 'فوطة صحية'),
    'Sanitery Napkin': () => tl('Sanitary Napkin', 'Serviette hygiénique', 'Compresa', 'فوطة صحية'),
    'Biscuit & Cookies': () => tl('Biscuit & Cookies', 'Biscuits et cookies', 'Galletas y cookies', 'بسكويت وكوكيز'),
    'Chips & Corn Flakes': () => tl('Chips & Corn Flakes', 'Chips et céréales', 'Patatas y cereales', 'رقائق وحبوب'),
    Chocolate: () => tl('Chocolate', 'Chocolat', 'Chocolate', 'شوكولاتة'),
    'Fruit Drink': () => tl('Fruit Drink', 'Boisson aux fruits', 'Bebida de fruta', 'مشروب فواكه'),
    'Ice Coffee': () => tl('Iced Coffee', 'Café glacé', 'Café helado', 'قهوة مثلجة'),
    'Iced Coffee': () => tl('Iced Coffee', 'Café glacé', 'Café helado', 'قهوة مثلجة'),
    Jams: () => tl('Jams', 'Confitures', 'Mermeladas', 'مربيات'),
    'Mineral Water': () => tl('Mineral Water', 'Eau minérale', 'Agua mineral', 'مياه معدنية'),
    Nectar: () => tl('Nectar', 'Nectar', 'Néctar', 'نكتار'),
    Noodle: () => tl('Noodle', 'Nouilles', 'Fideos', 'نودلز'),
    'Organic Juice': () => tl('Organic Juice', 'Jus bio', 'Zumo ecológico', 'عصير عضوي'),
    'Stuffed Grape Leaves': () => tl('Stuffed Grape Leaves', 'Feuilles de vigne farcies', 'Hojas de parra rellenas', 'ورق عنب محشي'),
    'Handkerchief - Pocket Tissue': () =>
      tl('Handkerchief - Pocket Tissue', 'Mouchoirs de poche', 'Pañuelos de bolsillo', 'مناديل جيب'),
    Handkerchief: () =>
      tl('Handkerchief - Pocket Tissue', 'Mouchoirs de poche', 'Pañuelos de bolsillo', 'مناديل جيب'),
    'Napkin - Facial Tissue': () =>
      tl('Napkins & Facial Tissues', 'Mouchoirs et essuie-tout', 'Servilletas y pañuelos faciales', 'مناديل ورقية'),
    'Napkins & Facial Tissues': () =>
      tl('Napkins & Facial Tissues', 'Mouchoirs et essuie-tout', 'Servilletas y pañuelos faciales', 'مناديل ورقية'),
    'Wet Baby Towel': () => tl('Baby Wet Wipes', 'Lingettes bébé', 'Toallitas húmedas para bebé', 'مناديل أطفال مبللة'),
    'Baby Wet Wipes': () => tl('Baby Wet Wipes', 'Lingettes bébé', 'Toallitas húmedas para bebé', 'مناديل أطفال مبللة'),
  };

  if (map[raw]) return map[raw]();
  const rawLower = raw.toLowerCase();
  for (const k of Object.keys(map)) {
    if (k.toLowerCase() === rawLower) return map[k]();
  }

  return raw;
}

/**
 * Kart altı açıklama: WooCommerce İngilizce şablonlarını seçilen dile çevirir (düz metin).
 */
export function translateCardDescriptionSnippet(plain, tl) {
  if (!plain) return '';
  let s = plain;

  const rep = (en, fr, es, ar) => {
    const tr = tl(en, fr, es, ar);
    if (en && tr) s = s.split(en).join(tr);
  };

  rep('Hard Durum Wheat', 'blé dur', 'trigo duro', 'قمح صلب');
  rep('100% Hard Durum Wheat', 'blé dur 100 %', 'trigo duro 100 %', 'قمح صلب 100٪');
  rep('%100 Hard Durum Wheat', 'blé dur 100 %', 'trigo duro 100 %', 'قمح صلب 100٪');

  rep(
    'Product Quality : %100 Hard Durum Wheat',
    'Qualité produit : blé dur 100 %',
    'Calidad : trigo duro 100 %',
    'جودة المنتج : قمح صلب 100٪'
  );
  rep(
    'Product Quality : %100 Hard Drum Wheat',
    'Qualité produit : blé dur 100 %',
    'Calidad : trigo duro 100 %',
    'جودة المنتج : قمح صلب 100٪'
  );
  rep(
    'Product Quality : % 100 fruit ratio',
    'Qualité : proportion fruit 100 %',
    'Calidad : proporción fruta 100 %',
    'الجودة : نسبة فاكهة 100٪'
  );
  rep('Product Quality :', 'Qualité produit :', 'Calidad :', 'الجودة :');
  rep('Product Sizes :', 'Formats produit :', 'Tamaños producto :', 'أحجام المنتج :');
  rep('Package Sizes :', 'Formats emballage :', 'Tamaños envase :', 'أحجام العبوة :');
  rep('Package Type :', "Type d'emballage :", 'Tipo de envase :', 'نوع العبوة :');
  rep('Package Types :', "Types d'emballage :", 'Tipos de envase :', 'أنواع العبوة :');
  rep('Package Details :', "Détails d'emballage :", 'Detalles de envase :', 'تفاصيل العبوة :');
  rep('Private Label Production : Available', 'Marque blanche : disponible', 'Marca privada : disponible', 'إنتاج بعلامة خاصة : متاح');
  rep('Private Label : Available', 'Marque blanche : disponible', 'Marca privada : disponible', 'علامة خاصة : متاحة');
  rep('Shelf Life :', 'DLC :', 'Vida útil :', 'مدة الصلاحية :');
  rep('Harvesting Time :', 'Période de récolte :', 'Época de cosecha :', 'وقت الحصاد :');
  rep('Durum Wheat', 'blé dur', 'trigo duro', 'قمح صلب');
  rep('Hard Durum', 'blé dur', 'trigo duro', 'قمح صلب');
  rep('Protein', 'Protéines', 'Proteínas', 'بروتين');
  rep('Gluten', 'Gluten', 'Gluten', 'غلوتين');
  rep('minimum', 'minimum', 'mínimo', 'حد أدنى');
  rep('maximum', 'maximum', 'máximo', 'حد أقصى');
  rep('Kraft Paper', 'papier kraft', 'papel kraft', 'ورق كرافت');
  rep('PP Sack', 'sac PP', 'saco PP', 'أكياس PP');

  return s;
}

/** Asia / Middle East bayrak satırı — ülke veya bölge adı (id ile sabit anahtar) */
export function translateFlagCountryLabel(id, englishName, tl) {
  const m = {
    'east-timor': () => tl('East Timor', 'Timor oriental', 'Timor Oriental', 'تيمور الشرقية'),
    'hong-kong': () => tl('Hong Kong', 'Hong Kong', 'Hong Kong', 'هونغ كونغ'),
    indonesia: () => tl('Indonesia', 'Indonésie', 'Indonesia', 'إندونيسيا'),
    laos: () => tl('Laos', 'Laos', 'Laos', 'لاوس'),
    malaysia: () => tl('Malaysia', 'Malaisie', 'Malasia', 'ماليزيا'),
    philippines: () => tl('Philippines', 'Philippines', 'Filipinas', 'الفلبين'),
    singapore: () => tl('Singapore', 'Singapour', 'Singapur', 'سنغافورة'),
    taiwan: () => tl('Taiwan', 'Taïwan', 'Taiwán', 'تايوان'),
    thailand: () => tl('Thailand', 'Thaïlande', 'Tailandia', 'تايلاند'),
    vietnam: () => tl('Vietnam', 'Vietnam', 'Vietnam', 'فيتنام'),
    jordan: () => tl('Jordan', 'Jordanie', 'Jordania', 'الأردن'),
    iraq: () => tl('Iraq', 'Irak', 'Irak', 'العراق'),
    uae: () => tl('UAE', 'Émirats arabes unis', 'EAU', 'الإمارات'),
    'saudi-arabia': () => tl('Saudi Arabia', 'Arabie saoudite', 'Arabia Saudí', 'السعودية'),
    lebanon: () => tl('Lebanon', 'Liban', 'Líbano', 'لبنان'),
    bahrain: () => tl('Bahrain', 'Bahreïn', 'Baréin', 'البحرين'),
    yemen: () => tl('Yemen', 'Yémen', 'Yemen', 'اليمن'),
    qatar: () => tl('Qatar', 'Qatar', 'Catar', 'قطر'),
    kuwait: () => tl('Kuwait', 'Koweït', 'Kuwait', 'الكويت'),
  };
  const fn = m[id];
  return fn ? fn() : englishName;
}
